/**
 * Admin Login Check Service
 * On admin login: send review reminders (once per day) and check for expired renewable contracts
 */

/**
 * Check if reminders were already sent today
 */
const haveRemindersSentToday = () => {
  const today = new Date().toISOString().split('T')[0];
  const lastReminderDate = localStorage.getItem('lastRemindersSentDate');
  return lastReminderDate === today;
};

/**
 * Mark reminders as sent today
 */
const markRemindersSentToday = () => {
  const today = new Date().toISOString().split('T')[0];
  localStorage.setItem('lastRemindersSentDate', today);
};

/**
 * Check pending reviews and send reminders to reviewers (once per day)
 */
export const checkAndSendReviewReminders = async (service) => {
  try {
    console.log('[ADMIN_CHECK] Checking for pending reviews...');

    // Only send reminders once per day
    if (haveRemindersSentToday()) {
      console.log('[ADMIN_CHECK] Reminders already sent today');
      return { remindersSent: 0, message: 'Reminders already sent today' };
    }

    // Get contracts in review
    const contractsData = await service.getListItems('Contracts');
    const contracts = contractsData?.value?.map(i => ({ ...i.fields, id: i.id })) || [];
    const inReviewContracts = contracts.filter(c => c.Status === 'In Review');

    if (inReviewContracts.length === 0) {
      console.log('[ADMIN_CHECK] No contracts in review');
      markRemindersSentToday();
      return { remindersSent: 0 };
    }

    let remindersSent = 0;

    // For each contract in review, get pending reviews
    for (const contract of inReviewContracts) {
      try {
        const reviewsData = await service.getListItems('ReviewStatus');
        const reviews = reviewsData?.value?.map(i => ({ ...i.fields, id: i.id })) || [];
        const pendingReviews = reviews.filter(
          r => r.ContractID === contract.id && r.Status !== 'Complete'
        );

        // Send reminder to each pending reviewer
        for (const review of pendingReviews) {
          try {
            await sendReviewReminderEmail(service, contract, review);
            remindersSent++;
            console.log(`[ADMIN_CHECK] Reminder sent to ${review.ReviewerEmail}`);
          } catch (error) {
            console.error(`[ADMIN_CHECK] Failed to send reminder to ${review.ReviewerEmail}:`, error);
          }
        }
      } catch (error) {
        console.error(`[ADMIN_CHECK] Error checking reviews for contract ${contract.RefNo}:`, error);
      }
    }

    // Mark that we sent reminders today
    markRemindersSentToday();

    console.log(`[ADMIN_CHECK] Sent ${remindersSent} reminders`);
    return { remindersSent };
  } catch (error) {
    console.error('[ADMIN_CHECK] Error in checkAndSendReviewReminders:', error);
    return { remindersSent: 0, error: error.message };
  }
};

/**
 * Send review reminder email to reviewer
 */
const sendReviewReminderEmail = async (service, contract, review) => {
  const subject = `📧 Reminder: Contract Review Pending - ${contract.RefNo}`;

  const emailBody = `
    <h2>Contract Review Reminder</h2>
    <p>You have a contract pending review that needs your attention.</p>

    <div style="background: #f5f5f5; padding: 15px; border-left: 4px solid #d32f2f; margin: 15px 0;">
      <strong>Contract Reference:</strong> ${contract.RefNo}<br/>
      <strong>Contract Title:</strong> ${contract.Title}<br/>
      <strong>Vendor:</strong> ${contract.VendorName}<br/>
    </div>

    <h3>What to do:</h3>
    <ol>
      <li>Go to the LexCore app</li>
      <li>Find this contract under "Contracts" tab</li>
      <li>Click "Start Review" when ready</li>
      <li>Review the Word document (with Track Changes enabled)</li>
      <li>Make any changes or comments as needed</li>
      <li>Click "Complete Review" when finished</li>
    </ol>

    <p style="color: #666; font-size: 12px;">
      Once you click "Complete Review", you will no longer have access to this contract.
    </p>
  `;

  try {
    await service.sendEmail([review.ReviewerEmail], subject, emailBody);
  } catch (error) {
    console.error(`[EMAIL] Failed to send reminder to ${review.ReviewerEmail}:`, error);
    throw error;
  }
};

/**
 * Get expired renewable contracts for popup
 */
export const getExpiredRenewableContracts = async (service) => {
  try {
    console.log('[ADMIN_CHECK] Checking for expired renewable contracts...');

    const contractsData = await service.getListItems('Contracts');
    const contracts = contractsData?.value?.map(i => ({ ...i.fields, id: i.id })) || [];

    console.log(`[ADMIN_CHECK] Total contracts fetched: ${contracts.length}`);

    const today = Date.now();
    console.log(`[ADMIN_CHECK] Today's timestamp: ${new Date(today).toISOString()}`);

    // Filter for expired and renewable contracts
    const expiredRenewable = [];

    for (const contract of contracts) {
      if (!contract.ExpiryDate) {
        console.log(`[ADMIN_CHECK] Skipping ${contract.RefNo || 'unknown'} - no ExpiryDate`);
        continue;
      }

      // Skip only if terminated or cancelled (not Expired, since renewable contracts might be renewed)
      if (['Terminated', 'Cancelled'].includes(contract.Status)) {
        console.log(`[ADMIN_CHECK] Skipping ${contract.RefNo} - Status is ${contract.Status}`);
        continue;
      }

      // Calculate days remaining (use Math.floor to properly handle negative values)
      // Negative = overdue, Positive = days remaining
      const daysLeft = Math.floor((new Date(contract.ExpiryDate) - today) / 86400000);
      console.log(`[ADMIN_CHECK] ${contract.RefNo}: daysLeft=${daysLeft}, IsRenewal=${contract.IsRenewal}, Status=${contract.Status}`);

      // Check if renewable (exact match to "Yes")
      const isRenewable = contract.IsRenewal === 'Yes' || contract.IsRenewal == 'Yes';

      // Include if it's renewable AND (already expired OR expiring within 60 days)
      // daysLeft <= 60 includes:
      // - daysLeft < 0 (already expired)
      // - daysLeft = 0 to 60 (expiring soon)
      if (isRenewable && daysLeft <= 60) {
        expiredRenewable.push({
          ...contract,
          daysLeft: daysLeft,
          DaysOverdue: daysLeft < 0 ? Math.abs(daysLeft) : 0
        });
        console.log(`[ADMIN_CHECK] ✓ Including ${contract.RefNo}`);
      }
    }

    // Sort by most urgent first (fewest days remaining)
    const sorted = expiredRenewable.sort((a, b) => a.daysLeft - b.daysLeft);

    console.log(`[ADMIN_CHECK] Found ${sorted.length} renewable contracts expiring within 60 days:`,
      sorted.map(c => ({
        RefNo: c.RefNo,
        IsRenewal: c.IsRenewal,
        Status: c.Status,
        ExpiryDate: c.ExpiryDate,
        daysLeft: c.daysLeft
      }))
    );

    return sorted;
  } catch (error) {
    console.error('[ADMIN_CHECK] Error getting expired renewable contracts:', error);
    return [];
  }
};
